/* eslint-disable no-console */
import { useBloc as useBloc2 } from "@blac/react";
import type { FC } from "react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useParams, useSearchParams } from "react-router-dom";
import reportErrorSentry from "src/lib/reportErrorSentry";
import useQuestionnaireCubitInContext from "src/state/QuestionnaireCubit/useQuestionnaireCubitInContext";
import QuestionnairePreviewCubit from "src/state/QuestionnairePreviewCubit/QuestionnairePreviewCubit";
import { StorageController } from "src/state/StorageBloc/StorageBloc";
import CustomQuestionnaire from "src/ui/components/CustomQuestionnaire/CustomQuestionnaire";
import CustomQuestionnairePreviewSteps from "src/ui/components/CustomQuestionnairePreview/CustomQuestionnairePreviewSteps";
import {
  ActionButton,
  Badge,
  CodeBlock,
  DangerButton,
  DebugPanelContainer,
  EndScreenItem,
  ExternalLink,
  FormGroup,
  FormLabel,
  Key,
  KeyValue,
  LogicActionHeader,
  LogicActionItem,
  LogicActionType,
  LogicCondition,
  LogicDescription,
  NoLogic,
  PanelHeader,
  PanelTitle,
  Section,
  SectionContent,
  SectionHeader,
  SectionTitle,
  TextArea,
  Toggle,
  Value,
  VariableItem,
  VariableName,
  VariableType,
  VariableValue
} from "src/ui/components/CustomQuestionnairePreview/debugPanel.styles";
import ConditionDisplay from "src/ui/components/CustomQuestionnairePreview/ConditionDisplay";
import { parseLogic } from "src/ui/components/CustomQuestionnairePreview/logicParser";
import useQuestionnaireTracking from "src/ui/hooks/useQuestionnaireTracking";
import Link from "src/ui/components/Link/Link";
import { CenterQuestionnaireContent } from "../AppQueryPopups/dialogs/LifestyleProgramDialog";

const LIFESTYLE_MODE_KEY = "hwj";
const LIFESTYLE_MODE_CHANGED_EVENT = "lifestyleModeChanged";

const formatPlaceholders = (text: string): string => {
  return text
    .replace(/{{var:/gm, "@")
    .replace(/{{field:/gm, "@")
    .replace(/{{hidden:/gm, "@")
    .replace(/}}/gm, "");
};

const Wrapper: FC<{
  children: React.ReactNode;
  isLifestyleQuestionnaire: boolean;
}> = ({ children, isLifestyleQuestionnaire }) => {
  return isLifestyleQuestionnaire ? (
    <CenterQuestionnaireContent style={{ display: "flex" }}>
      {children}
    </CenterQuestionnaireContent>
  ) : (
    <>{children}</>
  );
};

const VarsInput: FC = () => {
  const initial = useRef(sessionStorage.getItem("vars") ?? "{}");
  const handleInitialVarsChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    e.preventDefault();
    const { value } = e.target;
    try {
      JSON.parse(value);
      sessionStorage.setItem("vars", value);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <FormGroup>
      <FormLabel>Initial Variables (JSON)</FormLabel>
      <TextArea
        onChange={handleInitialVarsChange}
        defaultValue={initial.current}
      />
    </FormGroup>
  );
};

const CustomQuestionnaireDebugData: FC = () => {
  const [{ openViews }, { toggleView, isViewOpen }] = useBloc2(
    QuestionnairePreviewCubit
  );
  const [
    {
      formMeta = {},
      endScreens,
      logic,
      fields,
      customFormData,
      activeField,
      hiddenFields
    },
    cubit
  ] = useQuestionnaireCubitInContext();
  const {
    customFormVariables,
    generateOutputData,
    evaluateLogicActionConditions,
    runLogicConditions,
    runConditionLogic
  } = cubit;

  const evaluateCondition = useCallback(
    (condition: Parameters<typeof runConditionLogic>[0]) => {
      const conditionState = runLogicConditions(condition, fields);
      return runConditionLogic(condition, conditionState);
    },
    [runLogicConditions, runConditionLogic, fields]
  );

  const sectionProps = useCallback(
    (name: string) => ({
      open: isViewOpen(name),
      onClick: (e: React.MouseEvent<HTMLElement>) => {
        const target = e.target as HTMLElement;
        if (target.tagName === "SUMMARY" || target.closest("summary")) {
          e.preventDefault();
          toggleView(name);
        }
      }
    }),
    [openViews, isViewOpen, toggleView]
  );

  const {
    title,
    type,
    id: formId
  } = formMeta as Record<string, string[] | string>;

  const [answersJson, setAnswersJson] = useState({});
  const [showRawLogic, setShowRawLogic] = useState(false);

  const activeFieldLogicRaw = useMemo(() => {
    if (!activeField?.ref) return [];
    return logic.filter((item) => item.ref === activeField.ref);
  }, [activeField, logic]);

  const parsedLogic = useMemo(() => {
    return parseLogic(
      activeFieldLogicRaw,
      fields,
      endScreens,
      evaluateLogicActionConditions,
      evaluateCondition
    );
  }, [
    activeFieldLogicRaw,
    fields,
    endScreens,
    evaluateLogicActionConditions,
    evaluateCondition,
    customFormData
  ]);

  const activeFieldData = useMemo(() => {
    return JSON.stringify(activeField ?? {}, null, 2);
  }, [activeField]);

  const [isLifestyleQuestionnaire, setIsLifestyleQuestionnaire] = useState(
    () => localStorage.getItem(LIFESTYLE_MODE_KEY) === "true"
  );

  const handleIsLifestyleQuestionnaireChange = () => {
    const newValue = !isLifestyleQuestionnaire;
    if (newValue) {
      localStorage.setItem(LIFESTYLE_MODE_KEY, "true");
    } else {
      localStorage.removeItem(LIFESTYLE_MODE_KEY);
    }
    setIsLifestyleQuestionnaire(newValue);
    window.dispatchEvent(new CustomEvent(LIFESTYLE_MODE_CHANGED_EVENT));
  };

  return (
    <DebugPanelContainer>
      <PanelHeader>
        <PanelTitle>Questionnaire Debug</PanelTitle>
      </PanelHeader>

      <VarsInput />

      <Toggle>
        <input
          type="checkbox"
          checked={isLifestyleQuestionnaire}
          onChange={handleIsLifestyleQuestionnaireChange}
        />
        Lifestyle Questionnaire Mode
      </Toggle>

      <Section {...sectionProps("meta")}>
        <SectionHeader>
          <SectionTitle>Meta Data</SectionTitle>
        </SectionHeader>
        <SectionContent>
          <KeyValue>
            <Key>Title</Key>
            <Value>{title as string}</Value>
          </KeyValue>
          <KeyValue>
            <Key>Type</Key>
            <Value>{type as string}</Value>
          </KeyValue>
          <KeyValue>
            <Key>Form ID</Key>
            <Value>{formId as string}</Value>
          </KeyValue>
          <ExternalLink
            href={`https://admin.typeform.com/form/${formId?.toString()}/create`}
            target="_blank"
            rel="noreferrer"
          >
            Open in TypeForm
          </ExternalLink>
        </SectionContent>
      </Section>

      <Section {...sectionProps("vars")}>
        <SectionHeader>
          <SectionTitle>
            Variables
            <Badge>{Object.keys(customFormVariables).length}</Badge>
          </SectionTitle>
        </SectionHeader>
        <SectionContent>
          {Object.entries(customFormVariables).map(([name, value]) => (
            <VariableItem key={name}>
              <VariableName>{name}</VariableName>
              <VariableType>{typeof value}</VariableType>
              <VariableValue>{JSON.stringify(value)}</VariableValue>
            </VariableItem>
          ))}
        </SectionContent>
      </Section>

      <CustomQuestionnairePreviewSteps sectionProps={sectionProps("steps")} />

      <Section {...sectionProps("logic")}>
        <SectionHeader>
          <SectionTitle>
            Selected Step Logic
            {parsedLogic.length > 0 && (
              <Badge>{parsedLogic[0]?.actions.length ?? 0}</Badge>
            )}
          </SectionTitle>
        </SectionHeader>
        <SectionContent>
          {parsedLogic.length === 0 ? (
            <NoLogic>No logic rules for this step</NoLogic>
          ) : (
            <>
              {parsedLogic[0]?.actions.map((action, idx) => (
                <LogicActionItem
                  key={idx}
                  data-action={action.actionType}
                  data-trigger={action.wouldTrigger}
                  data-skipped={action.skippedByPriorJump}
                >
                  <LogicActionHeader>
                    <LogicActionType data-action={action.actionType}>
                      {action.actionType}
                    </LogicActionType>
                    <LogicDescription>{action.description}</LogicDescription>
                  </LogicActionHeader>
                  <LogicCondition>
                    <div style={{ marginBottom: 4 }}>When:</div>
                    <ConditionDisplay
                      condition={action.conditionTree}
                      fields={fields}
                      customFormVariables={customFormVariables}
                      customFormData={customFormData}
                      hiddenFields={hiddenFields}
                    />
                  </LogicCondition>
                </LogicActionItem>
              ))}
              <Toggle style={{ marginTop: 12 }}>
                <input
                  type="checkbox"
                  checked={showRawLogic}
                  onChange={() => setShowRawLogic(!showRawLogic)}
                />
                Show raw JSON
              </Toggle>
              {showRawLogic && (
                <CodeBlock>
                  {JSON.stringify(activeFieldLogicRaw, null, 2)}
                </CodeBlock>
              )}
            </>
          )}
        </SectionContent>
      </Section>

      <Section {...sectionProps("stepdata")}>
        <SectionHeader>
          <SectionTitle>Selected Step Data</SectionTitle>
        </SectionHeader>
        <SectionContent>
          <CodeBlock>{activeFieldData}</CodeBlock>
        </SectionContent>
      </Section>

      <Section {...sectionProps("endscreens")}>
        <SectionHeader>
          <SectionTitle>
            End Screens
            <Badge>{endScreens.length}</Badge>
          </SectionTitle>
        </SectionHeader>
        <SectionContent>
          {endScreens.map((item) => (
            <EndScreenItem
              key={item.id}
              data-active={activeField?.ref === item.ref}
            >
              <Link to={`#${item.ref}`}>{formatPlaceholders(item.title)}</Link>
            </EndScreenItem>
          ))}
        </SectionContent>
      </Section>

      <Section {...sectionProps("output")}>
        <SectionHeader>
          <SectionTitle>Output Data</SectionTitle>
        </SectionHeader>
        <SectionContent>
          <ActionButton onClick={() => setAnswersJson(generateOutputData)}>
            Generate Output
          </ActionButton>
          {Object.keys(answersJson).length > 0 && (
            <CodeBlock>{JSON.stringify(answersJson, null, 2)}</CodeBlock>
          )}
        </SectionContent>
      </Section>

      <Section {...sectionProps("tools")}>
        <SectionHeader>
          <SectionTitle>Tools</SectionTitle>
        </SectionHeader>
        <SectionContent>
          <DangerButton
            onClick={() => {
              StorageController.clear();
              window.location.reload();
            }}
          >
            Clear All Cache
          </DangerButton>
        </SectionContent>
      </Section>
    </DebugPanelContainer>
  );
};

const AddPartnerToCustomFormVariables: FC = () => {
  const [, { customFormVariables }] = useQuestionnaireCubitInContext();

  const [queryParams] = useSearchParams();
  const partner = queryParams.get("partner");

  useEffect(() => {
    if (partner?.length) {
      try {
        const decodedPartner = decodeURIComponent(partner ?? "");
        customFormVariables.partner = decodedPartner;
      } catch (error) {
        reportErrorSentry(error);
      }
    }
  }, [partner]);

  return null;
};

const PreviewLayout: FC<{ iframe: boolean }> = ({ iframe }) => {
  if (iframe) return null;

  return (
    <div
      className="hide-mobile"
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        width: 420,
        height: "100vh",
        overflow: "auto",
        zIndex: 1000
      }}
    >
      <CustomQuestionnaireDebugData />
    </div>
  );
};

const CustomQuestionnairePreview: FC<{ iframe?: boolean }> = ({
  iframe = false
}) => {
  const [state] = useBloc2(QuestionnairePreviewCubit);
  const { id = "" } = useParams<{ id: string }>();
  const preview = !iframe;

  const [isLifestyleQuestionnaire, setIsLifestyleQuestionnaire] = useState(
    () => localStorage.getItem(LIFESTYLE_MODE_KEY) === "true"
  );

  useEffect(() => {
    const handleChange = () => {
      setIsLifestyleQuestionnaire(
        localStorage.getItem(LIFESTYLE_MODE_KEY) === "true"
      );
    };
    window.addEventListener(LIFESTYLE_MODE_CHANGED_EVENT, handleChange);
    return () =>
      window.removeEventListener(LIFESTYLE_MODE_CHANGED_EVENT, handleChange);
  }, []);

  useQuestionnaireTracking();

  return (
    <div
      key={state.key}
      style={{
        position: "fixed",
        top: 0,
        bottom: 0,
        left: 0,
        right: iframe ? 0 : 420,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: iframe ? "#F2EFE7" : "#FFFAF2",
        overflow: "auto"
      }}
    >
      <Wrapper isLifestyleQuestionnaire={isLifestyleQuestionnaire}>
        <CustomQuestionnaire
          disableTracking
          preview={preview}
          id={id}
          useRouting
        >
          <AddPartnerToCustomFormVariables />
          <PreviewLayout iframe={iframe} />
        </CustomQuestionnaire>
      </Wrapper>
    </div>
  );
};

export default CustomQuestionnairePreview;
